import * as Popover from '@radix-ui/react-popover';
import * as Tabs from '@radix-ui/react-tabs';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HexAlphaColorPicker, HexColorInput, HexColorPicker } from 'react-colorful';
import { useField } from 'formik';

import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { useDebouncedCallback } from 'use-debounce';

import isHexColor from '@dashboard/services/isHexColor';

export default function ColorSelectDual({
  name,
  value = { light: '', dark: '' },
  picker = 'hexAlpha', // 'hexAlpha' or 'hex'
  onChange,
  currentColorMode = 'light',
  defaultColors,
  swatchClassName = 'h-5 w-5 rounded-full',
}) {
  const [activeTab, setActiveTab] = useState(currentColorMode);

  const lightColor = isHexColor(value?.light) ? value?.light : '';
  const darkColor = isHexColor(value?.dark) ? value?.dark : '';

  const [customColors, setCustomColors] = useState({
    light: lightColor,
    dark: darkColor,
  });

  useEffect(() => {
    setActiveTab(currentColorMode);
  }, [currentColorMode]);

  useEffect(() => {
    /*
      if (value?.light !== customColors.light || value?.
    dark !== customColors.dark) {
      setCustomColors({
        light: isHexColor(value?.light) ? value?.light
        : '',
        dark: isHexColor(value?.dark) ? value?.dark :
        '',
      });
    }
  }, [value, customColors]);
    */
    const newLight = isHexColor(value?.light) ? value?.light : '';
    const newDark = isHexColor(value?.dark) ? value?.dark : '';

    setCustomColors(prev => {
      if (newLight !== prev.light || newDark !== prev.dark) {
        return {
          light: newLight,
          dark: newDark,
        };
      }
      return prev;
    });
  }, [value?.light, value?.dark]);

  const debouncedSetCustomColors = useDebouncedCallback(onChange, 500, {
    leading: false,
    trailing: true,
    equalityFn: (a, b) => a.light === b.light && a.dark === b.dark,
  });

  const handlePointerUp = () => debouncedSetCustomColors.flush();

  const handleLightChange = v => {
    if (!isHexColor(v)) return;
    setCustomColors({
      ...customColors,
      light: v,
    });
    debouncedSetCustomColors({
      ...customColors,
      light: v,
    });
  };

  const handleDarkChange = v => {
    if (!isHexColor(v)) return;
    setCustomColors({
      ...customColors,
      dark: v,
    });
    debouncedSetCustomColors({
      ...customColors,
      dark: v,
    });
  };

  // determine which color to display in the trigger button
  // if no custom color is set, show the default color
  const getDisplayColor = () => {
    if (currentColorMode === 'dark') {
      return customColors.dark || defaultColors?.dark || '';
    }
    return customColors.light || defaultColors?.light || '';
  };

  const displayColor = getDisplayColor();

  return (
    <Popover.Root>
      <Popover.Trigger className="focus:outline-none block">
        <div
          className={clsx(
            swatchClassName,
            'flex items-center justify-center ring-1 ring-transparent dark:ring-foreground/25',
            {
              'bg-gray-100 dark:bg-muted': !displayColor,
            },
          )}
          style={{
            ...(displayColor && {
              background: displayColor,
              '--tw-ring-color':
                displayColor?.startsWith?.('#ffffff') || displayColor === 'transparent'
                  ? 'var(--color-gray-100)'
                  : '',
            }),
          }}
        />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content side="bottom" align="start" className="mt-2 z-10">
          <div
            className="bg-gray-50 dark:bg-card p-2 border border-gray-100 dark:border-border rounded-xs w-full"
            onPointerUp={handlePointerUp}
          >
            <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="w-full">
              {/* Tab triggers */}
              <Tabs.List className="flex w-full mb-2 gap-1">
                <Tabs.Trigger
                  value="light"
                  className={clsx(
                    'flex-1 flex items-center justify-center py-1.5 px-3 text-xs font-medium transition-colors focus:outline-none rounded bg-white dark:bg-background text-gray-800 dark:text-foreground',
                    'data-[state=active]:border-foreground data-[state=active]:border data-[state=active]:ring-1 data-[state=active]:ring-foreground',
                    'data-[state=inactive]:hover:bg-gray-50 dark:data-[state=inactive]:hover:bg-muted data-[state=inactive]:border data-[state=inactive]:border-gray-200 dark:data-[state=inactive]:border-border data-[state=inactive]:ring-1 data-[state=inactive]:ring-transparent',
                  )}
                >
                  <SunIcon className="w-4 h-4" />
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="dark"
                  className={clsx(
                    'flex-1 flex items-center justify-center py-1.5 px-3 text-xs font-medium transition-colors focus:outline-none rounded bg-white dark:bg-background text-gray-800 dark:text-foreground',
                    'data-[state=active]:border-foreground data-[state=active]:border data-[state=active]:ring-1 data-[state=active]:ring-foreground',
                    'data-[state=inactive]:hover:bg-gray-50 dark:data-[state=inactive]:hover:bg-muted data-[state=inactive]:border data-[state=inactive]:border-gray-200 dark:data-[state=inactive]:border-border data-[state=inactive]:ring-1 data-[state=inactive]:ring-transparent',
                  )}
                >
                  <MoonIcon className="w-4 h-4" />
                </Tabs.Trigger>
              </Tabs.List>

              {/* Light mode content */}
              <Tabs.Content value="light">
                <ColorPickerContent
                  color={customColors.light}
                  picker={picker}
                  onChange={handleLightChange}
                  name={`${name}.light`}
                  defaultColor={defaultColors?.light}
                />
              </Tabs.Content>

              {/* Dark mode content */}
              <Tabs.Content value="dark">
                <ColorPickerContent
                  color={customColors.dark}
                  picker={picker}
                  onChange={handleDarkChange}
                  name={`${name}.dark`}
                  defaultColor={defaultColors?.dark}
                />
              </Tabs.Content>
            </Tabs.Root>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// Reusable color picker content component
function ColorPickerContent({ color, onChange, name, defaultColor, picker }) {
  // Only use defaultColor if there's no color value at all (not even empty string)
  const displayColor = color ? color : defaultColor || '#ffffff';

  const ColorPickerComponent = picker === 'hexAlpha' ? HexAlphaColorPicker : HexColorPicker;

  return (
    <div className="flex flex-col">
      <ColorPickerComponent
        id={name}
        name={name}
        color={displayColor}
        onChange={v => {
          if (isHexColor(v)) {
            onChange(v);
          }
        }}
        style={{
          width: '100%',
        }}
      />

      <HexColorInput
        prefixed
        color={displayColor}
        onChange={v => {
          if (isHexColor(v)) {
            onChange(v);
          }
        }}
        className="mt-2 pl-2 block w-full border-gray-300 dark:border-border rounded-md font-mono text-gray-700 dark:text-muted-foreground p-2 border text-sm"
      />
    </div>
  );
}

// Memoized inner component - only re-renders when its props actually change
const ColorSelectDualMemo = React.memo(ColorSelectDual, (prevProps, nextProps) => {
  return (
    prevProps.name === nextProps.name &&
    prevProps.value?.light === nextProps.value?.light &&
    prevProps.value?.dark === nextProps.value?.dark &&
    prevProps.defaultColors?.light === nextProps.defaultColors?.light &&
    prevProps.defaultColors?.dark === nextProps.defaultColors?.dark &&
    prevProps.currentColorMode === nextProps.currentColorMode &&
    prevProps.picker === nextProps.picker &&
    prevProps.swatchClassName === nextProps.swatchClassName &&
    prevProps.onChange === nextProps.onChange
  );
});

// Formik field wrapper - subscribes to context but passes stable props to memoized inner
export const ColorSelectDualField = props => {
  const [field, , helpers] = useField(props.name || props);

  // Keep onChange stable via ref so the memoized component doesn't re-render
  const setValueRef = useRef(helpers.setValue);
  setValueRef.current = helpers.setValue;

  const handleChange = useCallback(value => setValueRef.current(value), []);

  return (
    <ColorSelectDualMemo
      name={props.name}
      value={field.value}
      onChange={handleChange}
      picker={props.picker}
      currentColorMode={props.currentColorMode}
      defaultColors={props.defaultColors}
      swatchClassName={props.swatchClassName}
    />
  );
};

ColorSelectDualField.displayName = 'ColorSelectDualField';
